import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ContactDropdownItem } from './useContactsDropdown';

/**
 * Hook para busca de contatos com debounce
 *
 * Busca contatos via endpoint contacts/dropdown e filtra localmente
 * por nome, email ou telefone. Debounce de 300ms no input.
 *
 * @param query - Texto de busca digitado pelo usuário
 * @returns Resultado da busca com loading state
 *
 * @example
 * ```tsx
 * const { data: contacts, isLoading } = useContactSearch(searchQuery);
 * ```
 */
export const useContactSearch = (query: string) => {
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce de 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return useQuery({
    queryKey: ['contacts-search', debouncedQuery],
    queryFn: async () => {
      const response = await api.get('contacts/dropdown');
      const allContacts = response.data.data as ContactDropdownItem[];

      // Filtrar localmente por nome, email ou telefone
      const searchLower = debouncedQuery.toLowerCase();
      return allContacts.filter((contact) =>
        contact.name?.toLowerCase().includes(searchLower) ||
        contact.email?.toLowerCase().includes(searchLower) ||
        contact.phone?.includes(debouncedQuery)
      );
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
